import type { CanonicalEntity, UniversalBundle } from '@/shared/api/types';
import {
  APARTMENT_SCENE_OBJECT_KINDS,
  HARDWARE_OBJECT_KINDS,
  MODULE_OBJECT_KINDS,
  PANEL_LIKE_OBJECT_KINDS,
} from './entityKinds';
import { isSketchupImportRootEntity, isSketchupSourceEntity, skpSectionModuleOwnerId } from './skpSectionOwnership';

type Domain = Record<string, unknown>;

export function entityDomain(entity: CanonicalEntity | null | undefined): Domain {
  return (entity?.domain && typeof entity.domain === 'object' ? entity.domain : {}) as Domain;
}

export function domainString(entity: CanonicalEntity | null | undefined, key: string): string {
  const value = entityDomain(entity)[key];
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

export function entityObjectKind(entity: CanonicalEntity | null | undefined): string {
  return domainString(entity, 'objectKind');
}

export function isModuleEntity(entity: CanonicalEntity | null | undefined): boolean {
  return MODULE_OBJECT_KINDS.has(entityObjectKind(entity).toLowerCase());
}

export function entityModuleOwnerId(
  entity: CanonicalEntity | null | undefined,
  entitiesById?: Map<string, CanonicalEntity>,
): string {
  if (!entity) return '';
  // Секция публикации SketchUp решает владение раньше остальных договоров:
  // меши `sketchup_decode` несут человеческую группировку только в sourcePath,
  // а их каноническое `moduleId` указывает на синтетический корень. Возврат
  // корня собирал всю кухню одним «модулем» и ломал клик/драг по корпусу.
  // Меш без контейнера и сам корень владельцами не становятся ('').
  if (isSketchupSourceEntity(entity) || isSketchupImportRootEntity(entity)) {
    return skpSectionModuleOwnerId(entity);
  }
  if (isModuleEntity(entity)) return entity.id;

  const directModuleId = String(
    entity.moduleId
      || entityDomain(entity).moduleId
      || entityDomain(entity).ownerModuleId
      || ((entityDomain(entity).moduleFill as Record<string, unknown> | undefined)?.moduleId)
      || '',
  ).trim();
  if (directModuleId) return directModuleId;

  const encodedModuleId = String(entity.id || '').split('::')[0];
  if (encodedModuleId && encodedModuleId !== entity.id && entitiesById?.has(encodedModuleId) && isModuleEntity(entitiesById.get(encodedModuleId))) {
    return encodedModuleId;
  }

  if (!entitiesById) return '';
  const seen = new Set<string>();
  let parentId = String(entity.parentId || '').trim();
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = entitiesById.get(parentId);
    if (!parent) return '';
    if (isModuleEntity(parent)) return parent.id;
    const parentModuleId = String(
      parent.moduleId
        || entityDomain(parent).moduleId
        || entityDomain(parent).ownerModuleId
        || ((entityDomain(parent).moduleFill as Record<string, unknown> | undefined)?.moduleId)
        || '',
    ).trim();
    if (parentModuleId) return parentModuleId;
    parentId = String(parent.parentId || '').trim();
  }
  return '';
}

/** Ordered ownership references shared by resolution and fail-closed consumers. */
export function entityMotionOwnerReferences(entity: CanonicalEntity): Array<{ id: unknown; explicitMotion: boolean }> {
  const domain = entityDomain(entity);
  const visibility = domain.visibilityContract;
  const visibilityContract = visibility && typeof visibility === 'object' ? visibility as Record<string, unknown> : {};
  return [
    { id: domain.motionOwnerId, explicitMotion: true },
    { id: visibilityContract.ownerId, explicitMotion: false },
    { id: domain.attachedFacadeId, explicitMotion: true },
    { id: domain.attachedFrontId, explicitMotion: true },
    { id: domain.ownerId, explicitMotion: false },
    { id: domain.attachedPanelId, explicitMotion: false },
    { id: entity.parentId, explicitMotion: false },
  ];
}

export function entityMotionOwnerId(
  entity: CanonicalEntity | null | undefined,
  entitiesById?: Map<string, CanonicalEntity>,
): string {
  if (!entity) return '';
  const domain = entityDomain(entity);
  const motion = domain.motionContract;
  if (motion && typeof motion === 'object') return entity.id;

  const ownerIds = entityMotionOwnerReferences(entity)
    .map(({ id }) => String(id || '').trim())
    .filter(Boolean);
  if (!ownerIds.length) return '';
  if (!entitiesById) return ownerIds[0];
  for (const ownerId of ownerIds) {
    const owner = entitiesById.get(ownerId);
    if (!owner) continue;
    const ownerMotion = entityDomain(owner).motionContract;
    if (ownerMotion && typeof ownerMotion === 'object') return owner.id;
  }
  return '';
}

export function collectEntityMotionOwnerIds(
  operatingIds: string[],
  entities: CanonicalEntity[],
  entitiesById: Map<string, CanonicalEntity>,
): string[] {
  const owners = new Set<string>();
  const addOwner = (entity: CanonicalEntity | undefined) => {
    const ownerId = entityMotionOwnerId(entity, entitiesById);
    if (ownerId) owners.add(ownerId);
  };
  for (const id of operatingIds) {
    const entity = entitiesById.get(id);
    addOwner(entity);
    if (!entity || !isModuleEntity(entity)) continue;
    for (const child of entities) {
      if (entityModuleOwnerId(child, entitiesById) !== entity.id) continue;
      addOwner(child);
    }
  }
  return [...owners];
}

export function entityDomainKind(entity: CanonicalEntity | null | undefined): string {
  return firstDomainString(entity, [
    'hardwareClass',
    'hardwareRole',
    'panelKind',
    'fillKind',
    'facadeKind',
    'drawerKind',
    'shellKind',
    'openingKind',
    'wallKind',
    'electricalKind',
    'sanitaryKind',
    'objectKind',
  ]);
}

export function entityDisplayLabel(entity: CanonicalEntity | null | undefined, fallback = 'Объект'): string {
  if (!entity) return fallback;
  const label = firstDomainString(entity, ['blockLabel', 'label', 'catalogName', 'materialName']);
  return String(entity.name || label || entity.id || fallback);
}

export function entityDisplayType(entity: CanonicalEntity | null | undefined): string {
  if (!entity) return '';
  return entityDomainKind(entity);
}

export function entityLayerKey(entity: CanonicalEntity | null | undefined, graphLayer?: string | null): string {
  return String(graphLayer || entityObjectKind(entity) || '').toLowerCase();
}

export function isWallFaceEntity(entity: CanonicalEntity | null | undefined): boolean {
  const domain = entityDomain(entity);
  return domain.objectKind === 'apartment_shell'
    && typeof domain.wallKind === 'string'
    && Boolean(domain.wallKind);
}

export function isHardwareEntity(entity: CanonicalEntity | null | undefined): boolean {
  return HARDWARE_OBJECT_KINDS.has(entityObjectKind(entity).toLowerCase());
}

/**
 * Щит без декоративных вставок.  `FurnitureSceneEntity.isPart()` отвечает на
 * тот же вопрос ДРУГИМ ответом — он включает `visual` (265 сущностей на
 * диске).  Расхождение старое и намеренно сохранено; общий у них теперь
 * только словарь.
 */
export function isPanelLikeEntity(entity: CanonicalEntity | null | undefined): boolean {
  return PANEL_LIKE_OBJECT_KINDS.has(entityObjectKind(entity).toLowerCase());
}

export function materialColorHex(entity: CanonicalEntity | null | undefined): string {
  const material = entityDomain(entity).material;
  if (!material || typeof material !== 'object') return '';
  const color = String((material as Record<string, unknown>).colorHex || '');
  return color;
}

function firstDomainString(entity: CanonicalEntity | null | undefined, keys: string[]): string {
  const domain = entityDomain(entity);
  for (const key of keys) {
    const value = domain[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

/**
 * Имя модуля по его идентификатору — из того же бандла, что и объект в сцене.
 *
 * Второго источника имён во вьюере нет: подписанные планы несут только идентификаторы, и
 * подпись у соседа, подпись у направляющей и строка выбора исхода вставки обязаны называть
 * один и тот же шкаф одинаково.
 */
export function moduleLabelLookup(
  bundle: UniversalBundle | null | undefined,
): (moduleId: string) => string {
  return (moduleId: string) => {
    const entity = bundle?.canonicalScene?.entities?.find((item) => item.id === moduleId);
    return entity ? entityDisplayLabel(entity, moduleId) : moduleId;
  };
}

/**
 * Опознаёт архитектурную сцену (квартира/здание) по её сущностям.
 *
 * Этот вопрос задавали три экрана — `SceneCanvas`, `ViewportHud` и
 * `useGltfLoader`, — и каждый нёс свою копию одного и того же списка видов.
 * Копии совпадали, но разъехались бы от первой же правки.
 *
 * Осторожно: `CatalogFlyout` и `ModuleLibraryFlyout` спрашивают ДРУГОЕ —
 * `entity.kind === 'room'`, — и на бандле, где комната помечена только
 * `domain.objectKind`, отвечают иначе.  Это расхождение не закрыто здесь.
 */
export function isApartmentSceneEntity(entity: CanonicalEntity | null | undefined): boolean {
  return APARTMENT_SCENE_OBJECT_KINDS.has(entityObjectKind(entity).toLowerCase());
}

export function entitiesFormApartmentScene(entities: readonly CanonicalEntity[] | null | undefined): boolean {
  return (entities ?? []).some(isApartmentSceneEntity);
}
